/* @jsx h */
import { defineComponent, ref, onMounted, h, computed } from "vue";
import { fetchProfileOrders } from "@app/api/client/index";
import type { ProfileOrder } from "@lemmonhq/types";
import { OrderHistoryCard } from "./components";
import { CardsSkeleton } from "../AccountSkeleton";
import { RouterLink, useRoute } from "vue-router";
import { useLocalization } from "@lemmonhq/uikit";

export const OrderHistorySubpage = defineComponent({
  setup() {
    const orders = ref<ProfileOrder[]>([]);
    const route = useRoute();
    const isLoading = ref(true);
    const error = ref<string | null>(null);
    const { t } = useLocalization();

    // Newest first; under a shop's account page only that shop's orders.
    const sortedOrders = computed(() => {
      const shopSlug = route.params.shopslug as string | undefined;
      return orders.value
        .filter((entry) => !shopSlug || entry.restaurant.slug === shopSlug)
        .sort(
          (a, b) =>
            new Date(b.order.created_at).getTime() -
            new Date(a.order.created_at).getTime(),
        );
    });

    const loadOrders = async () => {
      try {
        isLoading.value = true;
        error.value = null;
        // Includes POS orders where the cashier identified this customer.
        orders.value = await fetchProfileOrders();
      } catch (err) {
        error.value = t("customer.account.failed_to_load_orders");
        console.error("Error loading orders:", err);
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(() => {
      loadOrders();
    });

    return () => (
      <div class="">
        <div class="">
          <h2 class="text-2xl font-bold mb-2 flex items-center gap-2">
            <span class="material-symbols-outlined text-primary">history</span>
            {t("customer.account.order_history")}
          </h2>
          <p class="text-base-content/60">
            {t("customer.account.order_history_copy")}
          </p>
        </div>
        <div class="py-4">
          {/* Error State */}
          {error.value && (
            <div class="alert alert-error mb-6">
              <span class="material-symbols-outlined">error</span>
              <span class="font-medium">{error.value}</span>
            </div>
          )}

          {/* Loading State */}
          {isLoading.value ? (
            <div role="status">
              <span class="sr-only">
                {t("customer.account.loading_orders")}
              </span>
              <CardsSkeleton cards={3} />
            </div>
          ) : sortedOrders.value.length === 0 ? (
            /* Empty State */
            <div class="rounded-2xl border border-base-300 bg-base-200/40 px-6 py-14 text-center">
              <span class="mx-auto grid size-14 place-items-center rounded-2xl bg-base-200 text-base-content/30">
                <span
                  class="material-symbols-outlined text-3xl"
                  aria-hidden="true"
                >
                  receipt_long
                </span>
              </span>
              <h3 class="mt-4 text-lg font-bold text-base-content">
                {t("customer.account.no_orders")}
              </h3>
              <p class="mx-auto mt-1 mb-6 max-w-sm text-sm text-base-content/60">
                {t("customer.account.no_orders_copy")}
              </p>
              <RouterLink
                to={
                  route.params.shopslug
                    ? `/restaurant/${route.params.shopslug}`
                    : "/"
                }
                class="btn btn-primary btn-sm gap-2"
              >
                <span class="material-symbols-outlined text-lg">
                  restaurant
                </span>
                <span>{t("customer.account.browse_menu")}</span>
              </RouterLink>
            </div>
          ) : (
            /* Orders List */
            <div class="space-y-4">
              {sortedOrders.value.map((entry) => (
                <OrderHistoryCard
                  key={entry.order.id}
                  order={entry.order}
                  restaurantName={entry.restaurant.name}
                  currency={entry.restaurant.currency ?? "CHF"}
                  receiptGuid={entry.receipt_guid}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  },
});
